import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  getStorage,
  ref as firebaseStorageRef,
  uploadBytesResumable,
  getDownloadURL,
  updateMetadata,
} from 'firebase/storage';
import styles from './AudioUploadForm.module.scss';

const AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|m4a|flac|aac|aiff|aif|webm)$/i;

const isAudioFile = (file) =>
  file.type.startsWith('audio/') || AUDIO_EXTENSIONS.test(file.name);

const AudioUploadForm = forwardRef((props, formRef) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [dropError, setDropError] = useState('');
  const fileInputRef = useRef(null);

  const selectFile = useCallback((file) => {
    if (!file) return;
    if (!isAudioFile(file)) {
      setDropError('Please drop an audio file (mp3, wav, m4a, etc.)');
      return;
    }
    setDropError('');
    setSelectedFile(file);
    setFileName((prev) => prev || file.name);
  }, []);

  useImperativeHandle(
    formRef,
    () => ({
      selectFile,
      canAcceptDrop: () => uploadStatus !== 'uploading',
    }),
    [selectFile, uploadStatus]
  );

  const handleFileChange = (event) => {
    selectFile(event.target.files[0]);
    event.target.value = '';
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setDropError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileNameChange = (event) => {
    setFileName(event.target.value);
  };

  const handleFileUpload = () => {
    if (selectedFile && fileName) {
      const storage = getStorage();
      const storageRef = firebaseStorageRef(storage, fileName);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      setUploadStatus('uploading');

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          setUploadStatus('error');
          console.error('Error uploading file:', error);
        },
        async () => {
          try {
            const uploadDate = new Date().toISOString();
            await updateMetadata(storageRef, { customMetadata: { uploadDate } });
            await getDownloadURL(uploadTask.snapshot.ref);
            setSelectedFile(null);
            setFileName('');
            setUploadStatus('success');
            setUploadProgress(0);
          } catch (error) {
            console.error('Error updating metadata or getting download URL:', error);
            setUploadStatus('error');
          }
        }
      );
    }
  };

  const handleDismissMessage = () => {
    setUploadStatus('idle');
    setUploadProgress(0);
  };

  return (
    <div className={styles.form}>
      <div className={styles.field}>
        <label>Audio File</label>
        {selectedFile ? (
          <div className={styles.selectedFile}>
            <span className={styles.fileName}>{selectedFile.name}</span>
            <button
              type='button'
              onClick={handleClearFile}
              disabled={uploadStatus === 'uploading'}
              aria-label='Remove file'
            >
              ×
            </button>
          </div>
        ) : (
          <button
            type='button'
            className={styles.browseButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadStatus === 'uploading'}
          >
            Browse files
          </button>
        )}
        <input
          ref={fileInputRef}
          type='file'
          accept='audio/*'
          onChange={handleFileChange}
          className={styles.hiddenInput}
        />
        {dropError && <p className={styles.dropError}>{dropError}</p>}
      </div>

      <div className={styles.field}>
        <label>Track Name</label>
        <input
          className={styles.textInput}
          type='text'
          value={fileName}
          onChange={handleFileNameChange}
          placeholder='Enter a track name'
        />
      </div>

      {uploadStatus === 'uploading' && (
        <>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className={styles.progressLabel}>{Math.round(uploadProgress)}%</p>
        </>
      )}

      {uploadStatus === 'success' && (
        <div className={`${styles.statusMessage} ${styles.success}`}>
          <span>Upload successful</span>
          <button onClick={handleDismissMessage}>OK</button>
        </div>
      )}

      {uploadStatus === 'error' && (
        <div className={`${styles.statusMessage} ${styles.error}`}>
          <span>Upload failed — please try again</span>
          <button onClick={handleDismissMessage}>OK</button>
        </div>
      )}

      <button
        className={styles.uploadButton}
        onClick={handleFileUpload}
        disabled={!selectedFile || !fileName || uploadStatus === 'uploading'}
      >
        {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload'}
      </button>
    </div>
  );
});

AudioUploadForm.displayName = 'AudioUploadForm';

export default AudioUploadForm;
